import React, { useState } from "react";
import {
  Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails,
  Divider, Chip, List, ListItem, ListItemIcon, ListItemText, TextField, InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CheckroomIcon from "@mui/icons-material/Checkroom";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import GroupsIcon from "@mui/icons-material/Groups";

const sections = [
  {
    id: "gruppen",
    title: "Gruppen / Bereiche",
    icon: <GroupsIcon />,
    color: "#7c4dff",
    content: [
      {
        subtitle: "Multi-Gruppen-System",
        text: `Die App unterstuetzt mehrere unabhaengige Gruppen (z.B. "Ballkinder", "Linienrichter", "Volunteers"). Jede Gruppe hat eigene Mitglieder, Einteilungen, Anwesenheitsdaten und Konfiguration.`,
      },
      {
        subtitle: "Gruppe wechseln",
        text: "Oben in der Seitenleiste befindet sich ein Dropdown, ueber das zwischen den Gruppen gewechselt werden kann. Alle Seiten zeigen dann automatisch die Daten der gewaehlten Gruppe.",
      },
      {
        subtitle: "Neue Gruppe erstellen",
        text: `Unter Admin-Tools > "Gruppen verwalten" kann eine neue Gruppe erstellt werden. Jede Gruppe bekommt einen Namen, einen Singular-Label und eigene Feature-Einstellungen.`,
      },
      {
        subtitle: "Individuelle Konfiguration",
        text: `Jede Gruppe hat eigene Einstellungen: Turniertage, Stundenlohn, Tagessatz-Optionen, Bonus-Optionen, Max. Freikarten. Diese werden unter Admin-Tools > Konfiguration eingestellt und gelten nur fuer die aktive Gruppe.`,
      },
      {
        subtitle: "Features pro Gruppe",
        text: "Nicht jede Gruppe braucht alle Features. Unter Admin-Tools koennen Features wie Platzeinteilung, Freikarten, Essensmarken, Einkleidung, Abrechnung etc. einzeln pro Gruppe aktiviert/deaktiviert werden. Deaktivierte Features verschwinden aus der Navigation.",
      },
    ],
  },
  {
    id: "mitglieder",
    title: "Mitglieder-Verwaltung",
    icon: <PeopleIcon />,
    color: "#29b6f6",
    content: [
      {
        subtitle: "Mitglieder importieren",
        text: `Unter Admin-Tools > "Mitglieder importieren" kann eine Excel-Datei (.xlsx) hochgeladen werden. Die Datei sollte Spalten "Vorname" + "Nachname" oder eine einzelne "Name"-Spalte enthalten. Es wird eine Vorschau angezeigt, bevor der Import bestaetigt wird. Alle importierten Mitglieder werden automatisch fuer alle konfigurierten Turniertage als verfuegbar markiert.`,
      },
      {
        subtitle: "Mitglieder-Uebersicht",
        text: `Die Uebersichtsseite zeigt alle Mitglieder in einer Tabelle mit Name, Netz-Checkbox und Einsatzanzahl. Klick auf eine Zeile oeffnet ein Detail-Drawer mit: Einteilungen, Check-in-Zeiten, Freikarten, Verfuegbarkeit, Kleidungsstatus und Kommentaren.`,
      },
      {
        subtitle: "Netz-Status",
        text: "Ueber die Checkbox in der Uebersicht kann markiert werden, ob ein Mitglied fuer den Netzdienst geeignet ist. Dieser Status wird in der Platzeinteilung angezeigt.",
      },
      {
        subtitle: "Kommentare",
        text: "Zu jedem Mitglied koennen Kommentare hinterlassen werden (mit Zeitstempel und Autor). Diese sind sowohl in der Mitglieder-Uebersicht als auch in der Platzeinteilung sichtbar.",
      },
      {
        subtitle: "Datenexport",
        text: `Ueber "ALLE DATEN EXPORTIEREN" auf der Uebersichtsseite werden saemtliche Daten (Mitglieder, Freikarten, Check-Ins, Einteilungen etc.) als Excel-Datei heruntergeladen.`,
      },
    ],
  },
  {
    id: "anwesenheit",
    title: "Anwesenheit & Check-In/Out",
    icon: <EventNoteIcon />,
    color: "#66bb6a",
    content: [
      {
        subtitle: "Anwesenheitsplan",
        text: `Die Anwesenheitsseite (/schedule) zeigt eine Matrix aller Mitglieder x Turniertage. Ueber "Bearbeitung starten" koennen Verfuegbarkeiten per Checkbox gesetzt werden. Optional kann eine Startzeit ("ab wann verfuegbar") pro Mitglied/Tag eingetragen werden.`,
      },
      {
        subtitle: "Check-In / Check-Out",
        text: `Ueber den Button "Zu CheckINs/OUTs" oeffnet sich ein Drawer mit drei Tabs:`,
      },
      {
        subtitle: "Tab 1: Check-In/Out",
        text: `Zeigt alle verfuegbaren Mitglieder fuer den gewaehlten Tag. Pro Person gibt es einen Check-In (gruen) oder Check-Out (rot) Button. "Alle auschecken" checkt alle eingecheckten Personen auf einmal aus. Neben jedem eingecheckten Mitglied werden die Check-In/Out-Zeiten angezeigt.`,
      },
      {
        subtitle: "Tab 2: Anwesend",
        text: `Zeigt in Echtzeit, welche Mitglieder aktuell eingecheckt sind (mit Uhrzeit). Von hier kann direkt ausgecheckt werden. Die Anzahl der Anwesenden wird im Tab-Label angezeigt.`,
      },
      {
        subtitle: "Tab 3: Crew/Platz Checkout",
        text: `Ermoeglicht das Auschecken ganzer Crews oder kompletter Plaetze auf einmal. Zeigt alle Plaetze mit ihren Crew-Zuordnungen. "Crew auschecken" checkt alle Mitglieder einer Crew aus, "Platz auschecken" alle Crews eines Platzes.`,
      },
      {
        subtitle: "Manuelle Zeitkorrektur",
        text: `Falls ein Check-In/Out-Fehler passiert ist, kann ueber das Stift-Icon neben jedem Mitglied ein Dialog geoeffnet werden, in dem Check-In und Check-Out-Zeiten manuell korrigiert werden koennen.`,
      },
    ],
  },
  {
    id: "platzeinteilung",
    title: "Platzeinteilung & Crews",
    icon: <SportsTennisIcon />,
    color: "#5b8def",
    content: [
      {
        subtitle: "Platzeinteilung",
        text: "Auf der Platzeinteilungsseite werden Mitglieder den Crews (A, B, C, D) auf den verschiedenen Courts zugeordnet. Pro Court und Tag gibt es bis zu 4 Crews.",
      },
      {
        subtitle: "Einteilung bearbeiten",
        text: `Ueber die "Einteilung Edit"-Seite koennen Mitglieder per Drag-and-Drop oder Auswahl den Crews zugewiesen werden. Die Verfuegbarkeit und Startzeit jedes Mitglieds wird dabei angezeigt.`,
      },
      {
        subtitle: "Crew einwechseln",
        text: `Auf der Platzeinteilungsseite kann ueber den Button "Crew X einwechseln" eine Crew als aktiv markiert werden. Der Wechsel wird mit Zeitstempel protokolliert und im Crew-Wechselverlauf angezeigt.`,
      },
      {
        subtitle: "Einteilungen veroeffentlichen",
        text: `Ueber die Mitglieder-Uebersicht koennen alle Einteilungen auf einmal veroeffentlicht werden. Jedes Mitglied sieht dann auf seiner persoenlichen Seite, wann und wo es eingeteilt ist.`,
      },
    ],
  },
  {
    id: "crew-einsatzplan",
    title: "Crew-Einsatzplanung",
    icon: <AccessTimeIcon />,
    color: "#42a5f5",
    content: [
      {
        subtitle: "Zwei Modi",
        text: `Die Crew-Einsatzplanung (/court-start-times) bietet zwei Modi: "Einfach" und "Flexibel".`,
      },
      {
        subtitle: "Einfacher Modus",
        text: "Pro Court wird eine Startzeit, Crew-Anzahl (2 oder 3) und Turnus (45 oder 60 Minuten) eingestellt. Die Crews rotieren automatisch in gleichmaessigen Intervallen.",
      },
      {
        subtitle: "Flexibler Modus (Crews teilen Plaetze)",
        text: `Im flexiblen Modus koennen 2-4 Crews frei auf verschiedene Plaetze und Zeitfenster verteilt werden. Beispiel: Crew A spielt auf Court 1 von 12:00-13:00, hat dann Pause, und spielt auf Court 2 von 14:00-15:00.`,
      },
      {
        subtitle: "Einsaetze konfigurieren",
        text: `Pro Crew koennen beliebig viele Einsaetze hinzugefuegt werden. Jeder Einsatz hat einen Platz (Court) und einen Zeitraum (Von/Bis). Die Tagesansicht zeigt farbcodiert, welche Crew wann auf welchem Platz ist.`,
      },
      {
        subtitle: "Plan zwischen Tagen kopieren",
        text: `Ein erstellter Crew-Plan kann von einem Tag auf einen anderen uebertragen werden, um nicht jeden Tag neu konfigurieren zu muessen.`,
      },
      {
        subtitle: "Crew-Startzeiten anzeigen",
        text: "Die Crew-Startzeiten-Seite (/court_crew_times) zeigt den konfigurierten Plan als Tagesansicht - entweder im einfachen oder flexiblen Format, je nachdem was konfiguriert ist.",
      },
    ],
  },
  {
    id: "abrechnung",
    title: "Abrechnung",
    icon: <ReceiptIcon />,
    color: "#e6c06a",
    content: [
      {
        subtitle: "Stunden-basierte Berechnung",
        text: "Die Abrechnung basiert auf den Check-In/Out-Zeiten. Fuer jeden Tag wird die Arbeitszeit automatisch berechnet. Je nach Schwellenwert erhaelt das Mitglied den vollen oder halben Tagessatz.",
      },
      {
        subtitle: "Tagessaetze pro Tag",
        text: `Pro Turniertag kann ein individueller Tagessatz aus den konfigurierten Optionen gewaehlt werden. Ueber "Alle X,XX" Buttons koennen alle Tage auf einmal gesetzt werden.`,
      },
      {
        subtitle: "Schwellenwert",
        text: "Der Schwellenwert (in Stunden) bestimmt, ab wann der volle Tagessatz gezahlt wird. Unter dem Schwellenwert (aber >= 1h) wird der halbe Satz gezahlt. Unter 1h gibt es keine Verguetung.",
      },
      {
        subtitle: "Bonus pro Person",
        text: "Jedes Mitglied kann einen individuellen Bonus erhalten (aus den konfigurierten Bonus-Optionen). Der Bonus wird zum Gesamtbetrag addiert.",
      },
      {
        subtitle: "Einstellungen pro Gruppe",
        text: "Alle Abrechnungseinstellungen (Tagessaetze, Schwellenwert, Boni) werden separat pro Gruppe gespeichert. Beim Wechsel zwischen Gruppen werden die jeweiligen Einstellungen geladen.",
      },
      {
        subtitle: "Export & Veroeffentlichung",
        text: `"Export Uebersicht" erstellt eine Excel-Datei mit allen Abrechnungsdaten. "Stundenzettel" generiert pro Person einen detaillierten Stundenzettel (erfordert vorherige E-Mail-Zuordnung per Excel-Upload). "An Ballkinder veroeffentlichen" macht die Abrechnung auf der persoenlichen Mitgliederseite sichtbar.`,
      },
    ],
  },
  {
    id: "freikarten",
    title: "Freikarten",
    icon: <ConfirmationNumberIcon />,
    color: "#ef5350",
    content: [
      {
        subtitle: "Freikarten verwalten",
        text: "Pro Mitglied und Tag koennen Freikarten vergeben werden. Die Gesamtanzahl pro Mitglied ist durch die konfigurierte Maximalzahl begrenzt.",
      },
      {
        subtitle: "Bestaetigung",
        text: "Freikartenanfragen koennen pro Tag bestaetigt oder abgelehnt werden.",
      },
    ],
  },
  {
    id: "essensmarken",
    title: "Essensmarken",
    icon: <RestaurantIcon />,
    color: "#ffa726",
    content: [
      {
        subtitle: "Tagesuebersicht",
        text: "Pro Tag und Mitglied kann der Essensmarken-Status verwaltet werden (erhalten/nicht erhalten).",
      },
    ],
  },
  {
    id: "einkleidung",
    title: "Einkleidung",
    icon: <CheckroomIcon />,
    color: "#7e57c2",
    content: [
      {
        subtitle: "Kleidungsausgabe",
        text: "Auf der Einkleidungsseite wird fuer jedes Mitglied erfasst, ob die Kleidung ausgegeben wurde. Der Status wird auch in der Mitglieder-Uebersicht angezeigt.",
      },
    ],
  },
  {
    id: "abstimmungen",
    title: "Abstimmungen / Polls",
    icon: <HowToVoteIcon />,
    color: "#7c4dff",
    content: [
      {
        subtitle: "Polls erstellen",
        text: `Unter "Abstimmungen" koennen beliebige Polls erstellt werden mit eigenen Optionen. Polls koennen veroeffentlicht und ueber einen Link an Mitglieder verteilt werden.`,
      },
      {
        subtitle: "Ergebnisse",
        text: "Die Ergebnisse werden in Echtzeit angezeigt mit Anzahl der Stimmen pro Option.",
      },
    ],
  },
  {
    id: "mitgliederseite",
    title: "Persoenliche Mitgliederseite",
    icon: <SettingsApplicationsIcon />,
    color: "#26c6da",
    content: [
      {
        subtitle: "Konfiguration",
        text: `Ueber "Ballkind-Seite Einstellungen" wird die persoenliche Seite konfiguriert, die jedes Mitglied ueber seinen individuellen Link sieht. Einstellbar sind: Slider/Banner, Einteilungen anzeigen, Crew-Zeiten-Link, Treffpunkt-Hinweis, ATP-Spielplan-Link, Freikarten, Abrechnungsdaten, individuelle Ankuendigungen und benutzerdefinierte Links.`,
      },
      {
        subtitle: "Veroeffentlichen",
        text: "Aenderungen muessen explizit veroeffentlicht werden, um auf der Mitgliederseite sichtbar zu werden. So kann in Ruhe konfiguriert werden, bevor Aenderungen live gehen.",
      },
      {
        subtitle: "Mitglieder-Links",
        text: `Jedes Mitglied hat einen individuellen Link im Format: https://ballkid-management.de/{gruppe}/member/{id}. Die Links koennen ueber die Mitglieder-Uebersicht als Excel exportiert werden.`,
      },
    ],
  },
  {
    id: "admin",
    title: "Admin-Tools",
    icon: <AdminPanelSettingsIcon />,
    color: "#e53935",
    content: [
      {
        subtitle: "Zugriffsverwaltung",
        text: `Unter Admin-Tools wird verwaltet, welche E-Mail-Adressen auf die App zugreifen koennen. Rollen: "Admin" (voller Zugriff) und "Betrachter" (eingeschraenkt).`,
      },
      {
        subtitle: "Turnierkonfiguration",
        text: "Hier werden pro Gruppe eingestellt: Turniertage, Max. Freikarten, Stundenlohn, Tagessatz-Optionen und Bonus-Optionen.",
      },
      {
        subtitle: "Gefahrenzone",
        text: `"Alle Daten loeschen" entfernt saemtliche Daten der aktiven Gruppe unwiderruflich. Zur Sicherheit muss "LOESCHEN" eingegeben werden.`,
      },
    ],
  },
];

const DocumentationPage = () => {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(false);

  const filteredSections = search
    ? sections.filter((s) => {
        const q = search.toLowerCase();
        return (
          s.title.toLowerCase().includes(q) ||
          s.content.some((c) => c.subtitle.toLowerCase().includes(q) || c.text.toLowerCase().includes(q))
        );
      })
    : sections;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Dokumentation</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Ausfuehrliche Anleitung zu allen Funktionen der Ballkid-Management-App.
      </Typography>

      <TextField
        placeholder="Suche in der Dokumentation..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Quick Links */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Schnellnavigation</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {sections.map((s) => (
            <Chip
              key={s.id}
              icon={s.icon}
              label={s.title}
              size="small"
              onClick={() => {
                setExpanded(s.id);
                document.getElementById(`doc-${s.id}`)?.scrollIntoView({ behavior: "smooth" });
              }}
              sx={{ "& .MuiChip-icon": { color: s.color } }}
              variant="outlined"
            />
          ))}
        </Box>
      </Paper>

      {/* Content */}
      {filteredSections.map((section) => (
        <Accordion
          key={section.id}
          id={`doc-${section.id}`}
          expanded={expanded === section.id || !!search}
          onChange={(_, isExpanded) => setExpanded(isExpanded ? section.id : false)}
          sx={{ mb: 1, "&:before": { display: "none" } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ color: section.color }}>{section.icon}</Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{section.title}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {section.content.map((item, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {item.subtitle}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {item.text}
                </Typography>
                {i < section.content.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      {filteredSections.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">Keine Ergebnisse fuer "{search}"</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default DocumentationPage;
